import { useState, useEffect, useRef } from 'react';
import { FaAndroid, FaGlobe } from "react-icons/fa";

function useInView(threshold = 0.15) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, visible];
}

const HRM_MODULES = [
  { icon: '📊', color: '#ede9fe', name: 'Interactive Dashboard', desc: 'Live graphs of total headcount, open hiring positions and daily attendance. Birthday and work-anniversary alerts appear automatically so HR never misses a celebration.' },
  { icon: '👥', color: '#fef3c7', name: 'Employee Management', desc: 'Add, edit and manage every employee profile in one place — personal details, documents, designation, department and reporting manager.' },
  { icon: '📋', color: '#d1fae5', name: 'DSR / Task Reports', desc: 'Assign daily task reports (DSR) to employees. Team leads track completion in real time and managers get an instant view of team productivity.' },
  { icon: '📅', color: '#fce7f3', name: 'Company Calendar', desc: 'A shared calendar showing all scheduled company events, upcoming employee birthdays and work anniversaries — so your team is always aligned.' },
  { icon: '🕐', color: '#ffc4c4', name: 'Attendance Records', desc: 'View the complete monthly and historical attendance of every employee at a glance. Filter by date, department or individual.' },
  { icon: '🎉', color: '#e0f2fe', name: 'Events Management', desc: 'HR and management can create, edit and publish company events. Employees see events on their calendar and receive automatic notifications.' },
  { icon: '🏠', color: '#fef9c3', name: 'WFH Requests', desc: 'Employees submit work-from-home requests from their phone. Their TL or manager gets an instant notification and can approve or decline in one tap.' },
  { icon: '🤝', color: '#fce7f3', name: 'Client Visit Log', desc: 'Record every client who visits the office — purpose, project details and follow-up notes — so your business development pipeline stays organised.' },
  { icon: '🎯', color: '#ede9fe', name: 'Recruitment List', desc: 'HR tracks every candidate who has interviewed or is in the hiring pipeline. Status, notes and next steps are all in one searchable list.' },
  { icon: '🔍', color: '#d1fae5', name: 'Active Profiles', desc: 'See all job profiles for which the company is actively hiring — number of open seats, applications received and current stage of each role.' },
  { icon: '🏖', color: '#fef3c7', name: 'Leave Management', desc: 'Employees apply for leave on web. Managers approve or decline instantly. Balances update automatically across payroll and attendance.' },
  { icon: '⚙️', color: '#f1f5f9', name: 'System Settings', desc: 'Admins customise the entire platform — brand colour, logo, company name — and control settings across both the Entry System and ERP module from one panel.' },
];


const PLANS = [
  {
    id: 'monthly',
    name: 'Monthly',

    desc: 'Pay month-to-month, cancel anytime',
    features: [
      'Selfie & face-recognition attendance',
      'Payroll with PF, ESI & TDS processing',
      'Leave & WFH request management',
      'Live attendance dashboard & reports',
      'Cancel anytime, no lock-in',
    ],
  },
  {
    id: 'yearly',
    name: 'Yearly',

    desc: 'Billed annually',
    features: [
      'Everything in Monthly, billed yearly',
      'Multi-branch & department support',
      'Employee self-service portal (Staff App)',
      'Company calendar, events & DSR reports',
      'Priority support & onboarding assistance',
    ],
  },
  {
    id: 'members',
    name: 'Members',


    desc: 'Pricing scales with your team size',
    features: [
      'Flexible pricing per employee count',
      'All Entry System, ERP & payroll modules',
      'Candidate & hiring pipeline tracking',
      'Custom branding & system settings',
      'Selfie & face-recognition attendance'

    ],
  },
];

export default function Home() {
  const [entryRef, entryVisible] = useInView();
  const [hrmRef, hrmVisible] = useInView();
  const [expandedCard, setExpandedCard] = useState(null);
  const [callbackPlan, setCallbackPlan] = useState(null);
  const [selectedPlanId, setSelectedPlanId] = useState('yearly');

  return (
    <main>
      <section className="hero">
        {/* background blobs */}
        <div className="hero-blob hero-blob-1" />
        <div className="hero-blob hero-blob-2" />

        <div className="container" style={{ position: 'relative', zIndex: 1 }}>
          <div className="hero-grid">
            <div className="hero-content hero-content-anim">
              <div className="hero-pill">🚀 Trusted by 500+ businesses across India</div>
              <h1>
                Attendance <span className="hero-highlight">Easy,</span>
                <br />
                Growth <span className="hero-highlight">Crazy</span>
              </h1>
              <p>
                Complete ERP for Indian businesses — employee entry, attendance, payroll, and everything in between. Fast, simple, reliable.
              </p>
              <div className="hero-actions">

                <a href="tel:8302527500" className="btn btn-outline">
                  Talk to an Expert
                </a>
              </div>
              <div className="platforms">
                <span>Available on</span>
                <button
                  className="platform-icon"
                  onClick={() =>
                    document.getElementById("contact")?.scrollIntoView({
                      behavior: "smooth",
                    })
                  }
                >
                  <FaAndroid />
                </button>
                <button
                  className="platform-icon"
                  onClick={() =>
                    document.getElementById("contact")?.scrollIntoView({
                      behavior: "smooth",
                    })
                  }
                >
                  <FaGlobe />
                </button>
              </div>
              <div className="hero-trust">
                <span>✅ Free Demo Available</span>
                <span>✅ All Payment Methods Accepted</span>
                <span>✅ Setup Within 24 Hours</span>
              </div>
            </div>

            <div className="hero-images-wrap">
              <div className="hero-images">
                <div className="hero-img-card">
                  <img src="/imghero2.png" alt="Payroll" />
                  <div className="hero-float-badge hero-badge-top">
                    <span className="hfb-icon">📊</span>
                    <div>
                      <div className="hfb-val">Live ERP</div>
                      <div className="hfb-label">Dashboard</div>
                    </div>
                  </div>
                </div>
                <div className="hero-img-card hero-img-card-2">
                  <img src="/imghero.png" alt="Attendance" />
                  <div className="hero-float-badge hero-badge-bottom">
                    <span className="hfb-icon">✅</span>
                    <div>
                      <div className="hfb-val">Auto</div>
                      <div className="hfb-label">Attendance</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <section className="hrm-section" id="about-erp" ref={hrmRef}>
        <div className="container">
          <div className={`prod-header fade-up ${hrmVisible ? 'visible' : ''}`}>

            <h2>Complete ERP Module</h2>
            <p>An all-in-one HR management platform with 12 powerful modules — from an interactive live dashboard to leave management — all fully customisable to your brand.</p>
          </div>

          {/* Real Dashboard Screenshot */}
          <div className={`dashboard-preview fade-up ${hrmVisible ? 'visible' : ''}`} style={{ animationDelay: '.25s' }}>
            <div className="dashboard-preview-label">
              <span className="dp-badge">📸 Live Product Preview</span>
              <p>This is the actual PayraBook ERP Dashboard — real-time graphs, live data and fully interactive.</p>
            </div>
            <div className="dashboard-preview-img-wrap">
              <img src="/dashboard-preview.png" alt="PayraBook HRM Dashboard" className="dashboard-preview-img" />
            </div>
          </div>

          {/* Today's Attendance */}


          {/* 12-module feature grid */}
          <div className="hrm-feat-grid">
            {HRM_MODULES.map((m, i) => {
              const isExpanded = expandedCard === i;
              return (
                <div key={i} className={`hrm-feat-card fade-up ${hrmVisible ? 'visible' : ''}`}
                  style={{ animationDelay: `${.04 * i}s` }}>
                  <div className="hrm-feat-icon" style={{ background: m.color }}>{m.icon}</div>
                  <h4>{m.name}</h4>
                  <p>{isExpanded ? m.desc : `${m.desc.slice(0, 90)}…`}</p>
                  <button className="hrm-view-more" onClick={() => setExpandedCard(isExpanded ? null : i)}>
                    {isExpanded ? 'View less ▲' : 'View more ▼'}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </section>
      <section className="entry-section" ref={entryRef}>
        <div className="container">
          <div className={`prod-header fade-up ${entryVisible ? 'visible' : ''}`}>

            <h2>Smart Attendance System</h2>
            <p>Two powerful attendance modes — face recognition and manual entry — so every employee can clock in effortlessly, no matter their tech comfort level.</p>
          </div>

          {/* Row 1: Face Recognition — camera left, text right */}
          <div className={`entry-panel fade-up ${entryVisible ? 'visible' : ''}`} style={{ animationDelay: '.1s' }}>
            <div className="entry-panel-inner">
              <div className="camera-frame">
                <div className="camera-header">
                  <span className="cam-dot red" /><span className="cam-dot yellow" /><span className="cam-dot green" />
                  <span className="cam-title">PayraBook Entry · Face Recognition</span>
                  <span className="cam-live">● LIVE</span>
                </div>
                <div className="camera-body">
                  <div className="face-box">
                    <div className="face-corner tl" /><div className="face-corner tr" />
                    <div className="face-corner bl" /><div className="face-corner br" />
                    <span className="face-emoji">👤</span>
                    <div className="scan-line" />
                  </div>
                  <div className="cam-status"><span className="status-dot pulse" />Face detected — Priya S.</div>
                  <div className="face-match-bar"><div className="face-match-fill" /><span>Match 98.7 %</span></div>
                </div>
                <div className="camera-footer">
                  <div className="emp-chip">EMP-001 · Priya Sharma</div>
                  <button className="attend-btn">✅ Mark Attendance</button>
                </div>
              </div>
              <div className="entry-features">
                <div className="entry-mode-badge">📷 Mode 1</div>
                <h3>Face Recognition Attendance</h3>
                <ul className="feat-list">
                  <li><div className="feat-num">1</div><div><strong>Employee looks at the camera</strong><p>The system instantly captures the face frame — no employee ID needed.</p></div></li>
                  <li><div className="feat-num">2</div><div><strong>AI matches the face</strong><p>The recognition engine compares the live face against the registered database in under 1 second.</p></div></li>
                  <li><div className="feat-num">3</div><div><strong>Attendance marked automatically</strong><p>A time-stamped record is logged instantly. Zero manual effort from the employee.</p></div></li>
                </ul>
                <div className="feat-tags">
                  <span>⚡ &lt;1s recognition</span><span>🚫 Anti-spoofing</span><span>📶 Offline sync</span><span>🔒 Encrypted</span>
                </div>
              </div>
            </div>
          </div>

          {/* Row 2: Manual Attendance — text left, camera right */}
          <div className={`entry-panel fade-up ${entryVisible ? 'visible' : ''}`} style={{ animationDelay: '.25s' }}>
            <div className="entry-panel-inner reverse">
              <div className="camera-frame">
                <div className="camera-header">
                  <span className="cam-dot red" /><span className="cam-dot yellow" /><span className="cam-dot green" />
                  <span className="cam-title">PayraBook Entry · Manual Mode</span>
                </div>
                <div className="camera-body">
                  <div className="manual-id-box">
                    <label>Enter Employee ID</label>
                    <div className="manual-id-input">EMP-002<span className="cursor-blink">|</span></div>
                  </div>
                  <div className="face-box" style={{ marginTop: 12 }}>
                    <div className="face-corner tl" /><div className="face-corner tr" />
                    <div className="face-corner bl" /><div className="face-corner br" />
                    <span className="face-emoji">📸</span>
                  </div>
                  <div className="cam-status" style={{ color: '#f59e0b' }}><span className="status-dot" style={{ background: '#f59e0b' }} />Capturing face — Rahul V.</div>
                </div>
                <div className="camera-footer">
                  <div className="emp-chip">EMP-011 · Peehu Sharma</div>
                  <button className="attend-btn">✅ Confirm &amp; Mark</button>
                </div>
              </div>
              <div className="entry-features">
                <div className="entry-mode-badge manual">🪪 Mode 2</div>
                <h3>Manual Attendance</h3>
                <ul className="feat-list">
                  <li><div className="feat-num">1</div><div><strong>Employee enters their ID</strong><p>Staff type or select their Employee ID on the kiosk screen — simple and quick.</p></div></li>
                  <li><div className="feat-num">2</div><div><strong>Camera captures the face</strong><p>The system takes a live photo for identity verification.</p></div></li>
                  <li><div className="feat-num">3</div><div><strong>Attendance confirmed and logged</strong><p>Both the ID and face photo are saved as a verified time-stamped attendance record.</p></div></li>
                </ul>
                <div className="feat-tags">
                  <span>🪪 ID-based</span><span>📸 Photo proof</span><span>📝 Verified record</span><span>🕐 Time-stamped</span>
                </div>
              </div>

            </div>
          </div>

        </div>
      </section>
      {/* <section className="stats">
        <div className="container">
          <div className="stats-card">
            <div className="stat">
              <div className="stat-icon green">👥</div>
              <div>
                <div className="stat-num">80+ Lakhs</div>
                <div className="stat-label">Registered SMEs</div>
              </div>
            </div>
            <div className="stat">
              <div className="stat-icon blue">🚩</div>
              <div>
                <div className="stat-num">650+ Districts</div>
                <div className="stat-label">Presence in India</div>
              </div>
            </div>
            <div className="stat">
              <div className="stat-icon yellow">🌐</div>
              <div>
                <div className="stat-num">10+ Languages</div>
                <div className="stat-label">Used across India</div>
              </div>
            </div>
          </div>
        </div>
      </section> */}



      <section className="section feat-section">
        <div className="container">

          {/* Feature 1 — Attendance CSS Mockup */}
          <div className="feature">

            <div className="feature-img">
              <div className="feat-mockup" style={{ maxWidth: 580 }}>
                <div style={{ padding: '16px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e5e7eb' }}>
                  <span style={{ fontSize: 17, fontWeight: 800 }}>Attendance</span>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <span style={{ background: '#f1f5f9', border: '1px solid #e5e7eb', borderRadius: 8, padding: '6px 12px', fontSize: 12, color: '#6b7280' }}>🔍 Search</span>
                    <span style={{ background: '#f1f5f9', border: '1px solid #e5e7eb', borderRadius: 8, padding: '6px 12px', fontSize: 12, color: '#6b7280' }}>📅 Date</span>
                    <span style={{ background: '#f1f5f9', border: '1px solid #e5e7eb', borderRadius: 8, padding: '6px 12px', fontSize: 12, color: '#6b7280' }}>⬇</span>
                  </div>
                </div>
                <div style={{ overflow: 'hidden' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 10.5, tableLayout: 'fixed' }}>
                    <thead>
                      <tr style={{ background: 'linear-gradient(90deg,#3b5bdb,#7c3aed)' }}>
                        {['Date', 'ID', 'Name', 'In', 'Break In', 'Break Out', 'Out', 'Hours'].map(h => (
                          <th key={h} style={{ padding: '16px 5px', textAlign: 'left', color: '#fff', fontWeight: 600 }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        { date: '15 Jun', id: 'VID127', name: 'Himanshu K...', in: '11:39', breakIn: '13:30', breakOut: '14:00', out: '18:00', hrs: '7h 26m' },
                        { date: '15 Jun', id: 'VID110', name: 'Avdhesh', in: '11:38', breakIn: '13:15', breakOut: '13:45', out: '18:10', hrs: '6h 32m' },
                        { date: '15 Jun', id: 'VID195', name: 'peehu shar...', in: '11:21', breakIn: '14:00', breakOut: '14:30', out: '18:35', hrs: '7h 59m' },
                        { date: '13 Jun', id: 'VID127', name: 'Himanshu K...', in: '18:03', breakIn: '–', breakOut: '–', out: '19:30', hrs: '1h 26m' },
                      ].map((r, i) => (
                        <tr key={i} style={{ borderBottom: '1px solid #f1f5f9', background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                          <td style={{ padding: '16px 5px', color: '#6b7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.date}</td>
                          <td style={{ padding: '16px 5px', color: '#3b5bdb', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.id}</td>
                          <td style={{ padding: '16px 5px', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.name}</td>
                          <td style={{ padding: '16px 5px', color: '#10b981', fontWeight: 600, whiteSpace: 'nowrap' }}>{r.in}</td>
                          <td style={{ padding: '16px 5px', color: '#f59e0b', fontWeight: 600, whiteSpace: 'nowrap' }}>{r.breakIn}</td>
                          <td style={{ padding: '16px 5px', color: '#f59e0b', fontWeight: 600, whiteSpace: 'nowrap' }}>{r.breakOut}</td>
                          <td style={{ padding: '16px 5px', color: '#bb484d', fontWeight: 600, whiteSpace: 'nowrap' }}>{r.out}</td>
                          <td style={{ padding: '16px 5px', fontWeight: 700, whiteSpace: 'nowrap' }}>{r.hrs}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            <div className="feat-text">
              <div className="feat-label">📍 Attendance</div>
              <h2>Complete attendance records for every employee</h2>
              <p>View full attendance history for every employee — date-wise, with in/out times, break times, total working hours and photo proof.</p>
              <ul className="feat-bullets">
                <li>Date, Employee ID, Name, In & Out time</li>
                <li>Break In & Break Out time tracking</li>
                <li>Total Working Hours auto-calculated</li>
                <li>Photo proof captured at each punch</li>
                <li>Search by name & filter by date range</li>
                <li>Download attendance report as file</li>
              </ul>
            </div>
          </div>

          {/* Feature 2 — Salary CSS Mockup */}
          <div className="feature reverse">

            <div className="feature-img">
              <div className="feat-mockup" style={{ maxWidth: 580 }}>
                <div className="fm-header">
                  <span className="fm-title">💰 Salary Details — June 2026</span>
                </div>
                <div style={{ padding: '10px 16px 4px' }}>
                  <div style={{ background: '#f8fafc', borderRadius: 10, padding: '8px 14px', display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                    <div style={{ width: 32, height: 32, borderRadius: '50%', background: '#ffc4c4', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, color: '#bb484d' }}>P</div>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 700 }}>Priya Sharma</div>
                      <div style={{ fontSize: 11, color: '#6b7280' }}>EMP001 · Developer</div>
                    </div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginBottom: 10 }}>
                    {[
                      { label: 'Basic Pay', val: '₹20,000', color: '#1a1a2e' },
                      { label: 'HRA', val: '₹4,000', color: '#1a1a2e' },
                      { label: 'PF Deduction', val: '–₹2,400', color: '#bb484d' },
                      { label: 'ESI', val: '–₹350', color: '#bb484d' },
                      { label: 'Advance', val: '–₹1,500', color: '#bb484d' },
                      { label: 'Overtime', val: '+₹750', color: '#10b981' },
                    ].map((s, i) => (
                      <div key={i} style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: '6px 12px' }}>
                        <div style={{ fontSize: 11, color: '#6b7280' }}>{s.label}</div>
                        <div style={{ fontSize: 14, fontWeight: 700, color: s.color }}>{s.val}</div>
                      </div>
                    ))}
                  </div>
                  <div style={{ background: '#1a1a2e', borderRadius: 10, padding: '10px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                    <div>
                      <div style={{ fontSize: 11, color: 'rgba(255,255,255,.6)' }}>Net Pay</div>
                      <div style={{ fontSize: 22, fontWeight: 800, color: '#fff' }}>₹20,500</div>
                    </div>
                    <button style={{ background: 'var(--primary)', color: '#fff', border: 'none', borderRadius: 8, padding: '9px 16px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>📄 Payslip</button>
                  </div>
                </div>
              </div>
            </div>
            <div className="feat-text">
              <div className="feat-label">💰 Payroll</div>
              <h2>Salary, PF, advances and deductions</h2>
              <p>Handle payroll for monthly, daily, weekly, hourly or contract staff — all in one place, with zero manual calculations.</p>
              <ul className="feat-bullets">
                <li>PF, ESI & TDS auto-calculation</li>
                <li>Monthly & yearly salary breakdown</li>
                <li>Employee & Employer PF contribution management</li>
                <li>One-click salary editing and updates</li>

              </ul>
            </div>
          </div>

          {/* Feature 3 — Leave Management (mockup left, text right) */}
          <div className="feature reverse">
            <div className="feat-text">
              <div className="feat-label">🏖 Leave</div>
              <h2>Leave management made effortless</h2>
              <p>Employees can apply for leave in seconds. Their manager gets notified instantly and can approve or decline with a single tap.</p>
              <ul className="feat-bullets">
                <li>Employees submit leave requests on web</li>
                <li>Manager / TL gets instant notification</li>
                <li>One-tap approve or decline with remarks</li>

                <li>Full leave history per employee</li>
              </ul>
            </div>
            <div className="feature-img">
              <div className="feat-mockup" style={{ maxWidth: 580 }}>
                <div className="fm-header">
                  <span className="fm-title">🏖 Leave Requests</span>
                  <span className="notif-badge">3 pending</span>
                </div>
                <div className="fm-rows">
                  {[
                    { name: 'Rahul Verma', dates: '20–21 Jun', days: 2, type: 'Sick Leave', status: 'pending' },
                    { name: 'Sneha Patel', dates: '25 Jun', days: 1, type: 'Personal Leave', status: 'approved' },
                    { name: 'Arjun Mehta', dates: '28–30 Jun', days: 3, type: 'Casual Leave', status: 'pending' },
                    { name: 'Kavya Nair', dates: '2 Jul', days: 1, type: 'Work From Home', status: 'approved' },
                  ].map((r, i) => (
                    <div key={i} className="fm-row" style={{ flexWrap: 'wrap', gap: 6 }}>
                      <div className="fm-avatar">{r.name[0]}</div>
                      <div style={{ flex: 1, minWidth: 100 }}>
                        <div style={{ fontSize: 13, fontWeight: 600 }}>{r.name}</div>
                        <div style={{ fontSize: 11, color: '#6b7280' }}>{r.type} · {r.dates} ({r.days}d)</div>
                      </div>
                      {r.status === 'pending'
                        ? <div style={{ display: 'flex', gap: 6 }}>
                          <button style={{ background: '#d1fae5', color: '#065f46', border: 'none', borderRadius: 6, padding: '4px 10px', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>✓ Approve</button>
                          <button style={{ background: '#fce7f3', color: '#9d174d', border: 'none', borderRadius: 6, padding: '4px 10px', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>✗ Decline</button>
                        </div>
                        : <span className="fm-status paid">✓ Approved</span>
                      }
                    </div>
                  ))}
                </div>
                <div className="fm-footer" style={{ justifyContent: 'center', color: '#6b7280', fontSize: 12 }}>
                  Balances auto-update across attendance &amp; payroll
                </div>
              </div>
            </div>
          </div>

          {/* Feature 4 — Active Profiles (text left, table right) */}
          <div className="feature">
            <div className="feat-text">
              <div className="feat-label">🔍 Hiring</div>
              <h2>Manage active job profiles & hiring</h2>
              <p>Track every open job role the company is hiring for — skills required, total openings, how many are filled, and the current hiring status — all in one table.</p>
              <ul className="feat-bullets">
                <li>Add job profiles with required skills & experience</li>
                <li>Track total openings vs fulfilled openings</li>
                <li>Mark profiles as Open or Closed</li>
                <li>Attach job description (JD) to each profile</li>
                <li>Edit or remove profiles anytime</li>
              </ul>
            </div>
            <div className="feature-img">
              <div className="feat-mockup" style={{ maxWidth: 580 }}>
                <div style={{ padding: '18px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e5e7eb' }}>
                  <span style={{ fontSize: 18, fontWeight: 800 }}>Active Profiles</span>
                  <button style={{ background: '#3b5bdb', color: '#fff', border: 'none', borderRadius: 8, padding: '9px 18px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>+ Add Profile</button>
                </div>
                <div style={{ overflow: 'hidden' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, tableLayout: 'fixed' }}>
                    <thead>
                      <tr style={{ background: 'linear-gradient(90deg,#3b5bdb,#7c3aed)' }}>
                        {['Profile', 'Skills', 'Exp', 'Open', 'Filled', 'Status'].map(h => (
                          <th key={h} style={{ padding: '11px 12px', textAlign: 'left', color: '#fff', fontWeight: 600 }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        { profile: 'Python Dev', skills: 'Python, Java', exp: '1 yr', open: 10, filled: 2, status: 'Open' },
                        { profile: 'React Dev', skills: 'React, JS', exp: '2 yrs', open: 5, filled: 3, status: 'Open' },
                        { profile: 'HR Executive', skills: 'Recruiting', exp: '1 yr', open: 2, filled: 2, status: 'Closed' },
                        { profile: 'UI/UX Designer', skills: 'Figma, XD', exp: '2 yrs', open: 3, filled: 1, status: 'Open' },
                      ].map((r, i) => (
                        <tr key={i} style={{ borderBottom: '1px solid #f1f5f9', background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                          <td style={{ padding: '11px 12px', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 100 }}>{r.profile}</td>
                          <td style={{ padding: '11px 12px', color: '#6b7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.skills}</td>
                          <td style={{ padding: '11px 12px', color: '#6b7280' }}>{r.exp}</td>
                          <td style={{ padding: '11px 12px', fontWeight: 700 }}>{r.open}</td>
                          <td style={{ padding: '11px 12px', color: '#10b981', fontWeight: 700 }}>{r.filled}</td>
                          <td style={{ padding: '11px 12px' }}>
                            <span style={{ background: r.status === 'Open' ? '#d1fae5' : '#fce7f3', color: r.status === 'Open' ? '#065f46' : '#9d174d', padding: '3px 10px', borderRadius: 99, fontSize: 11, fontWeight: 700 }}>{r.status}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

        </div>
      </section>


      {/* ════════ PRODUCT 1 — ENTRY SYSTEM ════════ */}


      {/* ════════ PRODUCT 2 — HRM MODULE ════════ */}

      <section className="section prod-showcase-section" id="our-products">
        <div className="container">
          <h2 className="section-title">Our Products</h2>
          <p className="section-sub">Four powerful tools built for Indian businesses — attendance, payroll and HR management, all in one ecosystem.</p>
          <div className="prod-showcase-grid">

            <div className="prod-showcase-card" style={{ '--card-color': '#f59e0b', '--card-bg': '#fef3c7' }}>
              <div className="psc-top">
                <div className="psc-icon">📱</div>
                <div>
                  <div className="psc-tag">ENTRY SYSTEM</div>
                  <h3>Manual Attendance</h3>
                </div>
              </div>
              <p className="psc-desc">Complete attendance and payroll management in the palm of your hand — built for managers and business owners.</p>
              <ul className="psc-features">
                <li>✔ Employee ID (EID) based attendance entry</li>
                <li>✔ Secure numeric keypad for quick attendance</li>


                <li>✔ Real-time attendance history & time logs</li>
                <li>✔ Mark In, Out, Break-In & Break-Out with ease</li>
              </ul>

            </div>
            <div className="prod-showcase-card" style={{ '--card-color': '#10b981', '--card-bg': '#d1fae5' }}>
              <div className="psc-top">
                <div className="psc-icon">📷</div>
                <div>
                  <div className="psc-tag">Entry System</div>
                  <h3>Face Attendance</h3>
                </div>
              </div>
              <p className="psc-desc">AI-powered face recognition attendance on any Android camera — touchless, instant and fraud-proof entry for your office.</p>
              <ul className="psc-features">
                <li>✔ AI face recognition in under 1 second</li>
                <li>✔ Manual ID + photo attendance fallback</li>
                <li>✔ Live attendance dashboard & reports</li>


                <li>✔ Runs on any standard camera</li>
              </ul>

            </div>
            <div className="prod-showcase-card" style={{ '--card-color': '#bb484d', '--card-bg': '#ffc4c4' }}>
              <div className="psc-top">
                <div className="psc-icon">🖥</div>
                <div>
                  <div className="psc-tag">Desktop Software</div>
                  <h3>Desktop</h3>
                </div>
              </div>
              <p className="psc-desc">Powerful payroll software for HR teams — handles complex salary structures, statutory compliance and bulk payments with ease.</p>
              <ul className="psc-features">
                <li>✔ PF, ESI & TDS payroll processing</li>
                <li>✔ Multi-branch & department support</li>
                <li>✔ Attendance import & monthly reports</li>
                <li>✔ Employee records & document storage</li>


              </ul>

            </div>



            <div className="prod-showcase-card" style={{ '--card-color': '#6366f1', '--card-bg': '#ede9fe' }}>
              <div className="psc-top">
                <div className="psc-icon">👤</div>
                <div>
                  <div className="psc-tag">Employee ERP</div>
                  <h3>Staff</h3>
                </div>
              </div>
              <p className="psc-desc">Give every employee their own self-service portal — view payslips, apply for leave and track tasks.</p>
              <ul className="psc-features">

                <li>✔ View payslips & full salary history</li>
                <li>✔ Apply for leave & WFH requests</li>
                <li>✔ Submit daily task reports (DSR)</li>
                <li>✔ View own attendance & working hours</li>
                <li>✔ Instant push notifications & alerts</li>
              </ul>

            </div>

          </div>
        </div>
      </section>

      <section className="pricing-section" id="pricing">
        <div className="container">
          <div className="pricing-header">
            <h2>Simple, Transparent Pricing</h2>
            <p>
              Choose the perfect plan for your business and start managing
              attendance smarter.
            </p>
          </div>

          <div className="pricing-grid">
            {PLANS.map(plan => {
              const isSelected = selectedPlanId === plan.id;
              return (
                <div
                  key={plan.id}
                  className={`pricing-card ${isSelected ? 'selected' : ''}`}
                  onClick={() => setSelectedPlanId(plan.id)}
                >
                  {isSelected && <span className="popular-badge">Selected</span>}

                  <h3>{plan.name}</h3>

                  <div className={`price ${plan.price === 'Custom' ? 'custom' : ''}`}>
                    {plan.price} {plan.period && <span>{plan.period}</span>}
                  </div>

                  <p className="plan-desc">{plan.desc}</p>

                  <ul className="plan-features">
                    {plan.features.map((f, i) => (
                      <li key={i}>✓ {f}</li>
                    ))}
                  </ul>

                  <button
                    className={isSelected ? 'btn-filled-pricing' : 'btn-outline-pricing'}
                    onClick={(e) => { e.stopPropagation(); setSelectedPlanId(plan.id); setCallbackPlan(plan); }}
                  >
                    Request Callback
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </section>
      <TestimonialsSection />
      <CtaBanner />
      {callbackPlan && (
        <CallbackModal plan={callbackPlan} onClose={() => setCallbackPlan(null)} />
      )}
    </main>
  );
}

const TESTIMONIALS = [
  { initial: 'A', name: 'Aditya G.', role: 'Owner, Lifestyle Retail', color: '#ffc4c4', text: 'PayraBook has transformed how I manage my staff. The team is responsive and the product just works — I recommend it to everyone.' },
  { initial: 'S', name: 'Smit S.', role: 'Director, Coop Cabs', color: '#bfdbfe', text: 'Digitising attendance, salary calculation and payouts has saved us hours every month. A core part of our operations now.' },
  { initial: 'V', name: 'Vijay K.', role: 'Owner, Sai Ganesha Medicals', color: '#d1fae5', text: 'I have used it for over a year to manage 150+ staff across 5 branches. Auto attendance and online payments are gold.' },
  { initial: 'R', name: 'Ravi M.', role: 'HR Manager, TechNova Pvt Ltd', color: '#ede9fe', text: 'The leave and attendance tracking is spot on. Approvals happen in seconds and employees can see their records themselves. Big time saver.' },
  { initial: 'P', name: 'Priya D.', role: 'Owner, Bloom Salon Chain', color: '#fef3c7', text: 'Managing salary across 3 branches used to take 2 days. Now it takes 20 minutes. PayraBook is simply the best investment we made.' },
];

function TestimonialsSection() {
  const [active, setActive] = useState(0);
  const [animating, setAnimating] = useState(false);
  const [ref, visible] = useInView(0.1);
  const timerRef = useRef(null);

  const goTo = (idx) => {
    if (animating) return;
    setAnimating(true);
    setTimeout(() => {
      setActive(idx);
      setAnimating(false);
    }, 300);
  };

  useEffect(() => {
    timerRef.current = setInterval(() => {
      setAnimating(true);
      setTimeout(() => {
        setActive(prev => (prev + 1) % TESTIMONIALS.length);
        setAnimating(false);
      }, 300);
    }, 4000);
    return () => clearInterval(timerRef.current);
  }, []);

  const visible3 = [
    TESTIMONIALS[(active) % TESTIMONIALS.length],
    TESTIMONIALS[(active + 1) % TESTIMONIALS.length],
    TESTIMONIALS[(active + 2) % TESTIMONIALS.length],
  ];

  return (
    <section className="testi-section" id="reviews" ref={ref}>
      <div className="container">
        <div className={`fade-up ${visible ? 'visible' : ''}`}>

          <h2 className="section-title" style={{ marginBottom: 8 }}>Trusted By Leading Companies</h2>
          <p className="section-sub" style={{ marginBottom: 48 }}>Join the growing number of companies simplifying workforce management with PayraBook.</p>
        </div>
        <div className={`testi-cards ${animating ? 'testi-fade-out' : 'testi-fade-in'}`}>
          {visible3.map((t, i) => (
            <div key={`${active}-${i}`} className="testi-card" style={{ '--tc': t.color }}>
              <div className="testi-quote-icon">"</div>
              <p className="testi-text">{t.text}</p>
              <div className="testi-stars">{'★★★★★'}</div>
              <div className="testi-bottom">
                <div className="testi-avatar" style={{ background: t.color }}>{t.initial}</div>
                <div>
                  <div className="testi-name">{t.name}</div>
                  <div className="testi-role">{t.role}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="testi-dots">
          {TESTIMONIALS.map((_, i) => (
            <button key={i} className={`testi-dot ${i === active ? 'active' : ''}`} onClick={() => goTo(i)} />
          ))}
        </div>
      </div>
    </section>
  );
}

function CtaBanner() {
  const [ref, visible] = useInView(0.15);
  const [form, setForm] = useState({ name: '', phone: '', email: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  const handle = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    setError('');
    try {
      const res = await fetch('/.netlify/functions/send-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (res.ok) {
        setSubmitted(true);
      } else {
        setError('Something went wrong. Please try again.');
      }
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setSending(false);
    }
  };

  return (
    <section className="contact-section" id="contact" ref={ref}>
      <div className="container">
        <div className={`contact-wrap fade-up ${visible ? 'visible' : ''}`}>

          {/* Left info panel */}
          <div className="contact-info" style={{ '--contact-bg': "url('/imghero2.png')" }}>
            <div className="contact-tag">Book a Free Demo</div>
            <h2 className="contact-heading">Let's make HR simple for your business</h2>
            <p className="contact-sub">Drop your details and our team will call you back — no pressure, just a quick walkthrough of PayraBook built for your needs.</p>
            <div className="contact-points">
              <div className="cp-point"><span>🎯</span> Personalised demo for your business</div>
              <div className="cp-point"><span>⚡</span> Go live in less than 24 hours</div>
              <div className="cp-point"><span>📱</span> Works on Android & Web</div>
              <div className="cp-point"><span>🤝</span> Dedicated support team after onboarding</div>
            </div>
            <div className="contact-phone">
              <span>📞</span>
              <a href="tel:08095332013">(+91) 8302527500</a>
            </div>
          </div>

          {/* Right form */}
          <div className="contact-form-wrap">
            {submitted ? (
              <div className="contact-success">
                <div className="contact-success-icon">✅</div>
                <h3>Thank you!</h3>
                <p>We've received your message and will get back to you within 24 hours.</p>
              </div>
            ) : (
              <form className="contact-form" onSubmit={submit}>
                <div className="cf-field">
                  <label>Full Name</label>
                  <input name="name" type="text" placeholder="e.g. Rahul Sharma" value={form.name} onChange={handle} required />
                </div>
                <div className="cf-field">
                  <label>Contact Number</label>
                  <input name="phone" type="tel" placeholder="e.g. 98765 43210" value={form.phone} onChange={handle} required />
                </div>
                <div className="cf-field">
                  <label>Email Address</label>
                  <input name="email" type="email" placeholder="e.g. rahul@company.com" value={form.email} onChange={handle} required />
                </div>
                <div className="cf-field">
                  <label>Message / Query</label>
                  <textarea name="message" rows={3} placeholder="Tell us about your business and what you're looking for…" value={form.message} onChange={handle} />
                </div>
                {error && <p style={{ color: '#bb484d', fontSize: 13 }}>{error}</p>}
                <button type="submit" className="cf-submit" disabled={sending}>
                  {sending ? 'Sending…' : 'Send Message →'}
                </button>
              </form>
            )}
          </div>

        </div>
      </div>
    </section>
  );
}

function CallbackModal({ plan, onClose }) {
  const [form, setForm] = useState({ companyName: '', phone: '', email: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  const handle = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    setError('');
    try {
      const res = await fetch('/.netlify/functions/request-callback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, plan: plan.name }),
      });
      if (res.ok) {
        setSubmitted(true);
      } else {
        setError('Something went wrong. Please try again.');
      }
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box" onClick={e => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose} aria-label="Close">×</button>

        {submitted ? (
          <div className="contact-success">
            <div className="contact-success-icon">✅</div>
            <h3>Thank you!</h3>
            <p>Thank you for submitting your details, we will get back to you shortly.</p>
          </div>
        ) : (
          <>
            <h3 className="modal-title">Request a Callback — {plan.name} Plan</h3>
            <form className="contact-form" onSubmit={submit}>
              <div className="cf-field">
                <label>Company Name</label>
                <input name="companyName" type="text" placeholder="e.g. Sharma Enterprises" value={form.companyName} onChange={handle} required />
              </div>
              <div className="cf-field">
                <label>Phone Number</label>
                <input name="phone" type="tel" placeholder="e.g. 98765 43210" value={form.phone} onChange={handle} required />
              </div>
              <div className="cf-field">
                <label>Email Address</label>
                <input name="email" type="email" placeholder="e.g. rahul@company.com" value={form.email} onChange={handle} required />
              </div>
              <div className="cf-field">
                <label>Message</label>
                <textarea name="message" rows={3} placeholder="Tell us about your business…" value={form.message} onChange={handle} />
              </div>
              {error && <p style={{ color: '#bb484d', fontSize: 13 }}>{error}</p>}
              <button type="submit" className="cf-submit" disabled={sending}>
                {sending ? 'Sending…' : 'Submit →'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
